import React, { useEffect, useState, useCallback } from "react";
import { Accordion, Card } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import apiConstants from "../../../constans/apiConstants";
import axios from "axios";
import {
  toaster,
  httpRequest,
  httpRequestDelete,
} from "../../../utils/utils";
import Table from "react-bootstrap/Table";
import MobilePopUp from "../../MobilePopUp/MobilePopUp";
import { submitRoleAction } from "../../../store/actions/operationActions";
import {
  createApp,
  updateVendorPermissions,
} from "../../../store/actions/createApp";
import OperationsAccordionHeader from "../AccordionHeader";
import Pagination from "../../Pagination/pagination";

const PAGE_SIZE = 10;
const MOBILE_NUMBER_REGEX = /^\d{10}$/;

/*
 * Vendor permissions (Operations → Users, DEV only via isDevEnv):
 * - Permissions column, VENDOR role, Vendor Code field, permission modal/APIs.
 */

const isVendorRole = (role) =>
  String(role || "").trim().toUpperCase() === "VENDOR";

const authPath = (path) => `${apiConstants().AUTH_API_URL}api/v1/auth/${path}`;

const authHeaders = () => ({
  Accept: "application/json",
  "Content-Type": "application/json",
  Authorization: localStorage.getItem("token"),
});

const trimStr = (value) => (value == null ? "" : String(value).trim());

const fieldOrDash = (value) => trimStr(value) || "—";

const apiPayload = (responseData) =>
  responseData && responseData.body !== undefined
    ? responseData.body
    : responseData;

const resolveVendorCode = (item) => {
  if (!item) return "";
  return trimStr(item.code) || trimStr(item.encryptedCode);
};

const normalizePermissionList = (permissions) => {
  if (permissions == null) return [];
  if (Array.isArray(permissions)) {
    return permissions.map((p) => trimStr(p)).filter(Boolean);
  }
  if (typeof permissions === "string") {
    const trimmed = permissions.trim();
    return trimmed ? [trimmed] : [];
  }
  if (typeof permissions === "object") {
    return Object.keys(permissions).map((key) => trimStr(key)).filter(Boolean);
  }
  return [];
};

const vendorHasPermissions = (permissions) =>
  normalizePermissionList(permissions).length > 0;

const formatPermissions = (permissions) => {
  const list = normalizePermissionList(permissions);
  return list.length ? list.join(", ") : "—";
};

const pickVendorKeysFromBody = (body) => {
  if (!body || typeof body !== "object") return null;
  const accessKey =
    body.accessKey != null
      ? String(body.accessKey)
      : body.user && body.user.accessKey != null
      ? String(body.user.accessKey)
      : "";
  const secretKey =
    body.secretKey != null
      ? String(body.secretKey)
      : body.user && body.user.secretKey != null
      ? String(body.user.secretKey)
      : "";
  if (!accessKey && !secretKey) return null;
  return { accessKey, secretKey };
};

const buildVendorKeysCsv = (keys) => {
  const escape = (value) => `"${String(value).replace(/"/g, '""')}"`;
  return [
    ["Key", "Value"].map(escape).join(","),
    ["accessKey", keys.accessKey].map(escape).join(","),
    ["secretKey", keys.secretKey].map(escape).join(","),
  ].join("\n");
};

const permissionCodeFromSource = (source) => {
  // Auth may send permissionCode or derive it as "METHOD:/path".
  let code = trimStr(source.permissionCode) || trimStr(source.permission);
  const method = trimStr(source.method);
  const path = trimStr(source.path);
  if (!code && method && path) code = `${method}:${path}`;
  return code;
};

const normalizeApiDefinitions = (responseData) => {
  const payload = apiPayload(responseData);
  if (!payload) return [];
  const list = Array.isArray(payload) ? payload : Object.values(payload);
  return list
    .map((item, index) => {
      if (typeof item === "string") {
        const code = trimStr(item);
        return code
          ? { id: code, permissionCode: code, name: "—", service: "—" }
          : null;
      }
      if (!item || typeof item !== "object") return null;
      const permissionCode = permissionCodeFromSource(item);
      const id =
        permissionCode ||
        trimStr(item.id) ||
        trimStr(item.permission) ||
        trimStr(item.apiDefinition) ||
        trimStr(item.name) ||
        trimStr(item.code) ||
        `permission-${index}`;
      return {
        id,
        permissionCode: permissionCode || id,
        name: fieldOrDash(item.name),
        service: fieldOrDash(item.service),
      };
    })
    .filter(Boolean);
};

const vendorPermissionsList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.permissions)) return payload.permissions;
  if (payload && Array.isArray(payload.data)) return payload.data;
  if (payload && typeof payload === "object") return Object.values(payload);
  return [];
};

const normalizeVendorPermissions = (responseData) => {
  const payload = apiPayload(responseData);
  if (!payload) return [];
  return vendorPermissionsList(payload)
    .map((item) => {
      if (typeof item === "string") {
        const code = trimStr(item);
        return code
          ? { permissionCode: code, name: "—", service: "—" }
          : null;
      }
      if (!item || typeof item !== "object") return null;
      const source =
        item.apiDefinition && typeof item.apiDefinition === "object"
          ? { ...item.apiDefinition, ...item }
          : item;
      const permissionCode = permissionCodeFromSource(source);
      if (!permissionCode) return null;
      return {
        permissionCode,
        name: fieldOrDash(source.name),
        service: fieldOrDash(source.service),
      };
    })
    .filter(Boolean);
};

const enrichVendorPermissionRows = (rows, definitions) => {
  // Vendor permissions API may return codes only; fill name/service from api-definitions when missing.
  if (!rows.length || !definitions.length) return rows;
  const byCode = definitions.reduce((map, def) => {
    map[def.permissionCode] = def;
    return map;
  }, {});
  return rows.map((row) => {
    const def = byCode[row.permissionCode];
    if (!def) return row;
    return {
      ...row,
      name:
        row.name && row.name !== "—" ? row.name : def.name || row.name,
      service:
        row.service && row.service !== "—"
          ? row.service
          : def.service || row.service,
    };
  });
};

const loadErrorMessage = (err, fallback) =>
  (err && err.response && err.response.data && err.response.data.message) ||
  fallback;

const Users = () => {
  const [tableData, setTableData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [details, setDetails] = useState({
    name: "",
    mobileNumber: "",
    role: "SUPPORT",
  });
  const [showEditPopUp, setShowEditPopUp] = useState(false);
  const [editRowData, setEditRowData] = useState({});
  const [editRole, setEditRole] = useState("");
  const [searchUserValue, setSearchUserValue] = useState("");
  // Permissions modal state (generate vs update flow for VENDOR users).
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [permissionsVendor, setPermissionsVendor] = useState(null);
  const [apiDefinitions, setApiDefinitions] = useState([]);
  const [selectedPermissionCodes, setSelectedPermissionCodes] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [permissionsError, setPermissionsError] = useState("");
  const [permissionsModalIsUpdate, setPermissionsModalIsUpdate] = useState(false);
  const [permissionsVendorCode, setPermissionsVendorCode] = useState("");
  const [vendorExistingPermissions, setVendorExistingPermissions] = useState([]);
  const [permissionsAccordionKey, setPermissionsAccordionKey] = useState("0");
  const [permissionsModalIsRotate, setPermissionsModalIsRotate] = useState(false);
  const [rotatedVendorKeys, setRotatedVendorKeys] = useState(null);
  const [rotateAfterPermissionsLoad, setRotateAfterPermissionsLoad] =
    useState(false);

  useEffect(() => {
    setEditRole(editRowData.role);
  }, [editRowData]);

  useEffect(() => {
    if (showPermissionsModal) {
      setPermissionsAccordionKey("0");
    }
  }, [showPermissionsModal, permissionsModalIsUpdate]);

  // Load permission definitions (and existing vendor permissions in update mode) when modal opens.
  useEffect(() => {
    if (!isDevEnv || !showPermissionsModal || !permissionsVendor) {
      return undefined;
    }

    let cancelled = false;
    setPermissionsLoading(true);
    setPermissionsError("");
    setApiDefinitions([]);
    setSelectedPermissionCodes([]);
    setVendorExistingPermissions([]);
    const isUpdateMode = permissionsModalIsUpdate;
    const isRotateMode = permissionsModalIsRotate;
    const needsVendorPermissions = isUpdateMode || isRotateMode;
    const vendorCode = needsVendorPermissions ? permissionsVendorCode.trim() : "";

    if (needsVendorPermissions && !vendorCode) {
      setPermissionsError("Vendor code is required to load permissions.");
      setPermissionsLoading(false);
      return () => {
        cancelled = true;
      };
    }

    //get api definitions or permissions
    const definitionsRequest = axios.get(
      authPath("vendor/api-definitions"),
      { headers: authHeaders() }
    );

    const vendorPermissionsRequest =
      needsVendorPermissions && vendorCode
        ? axios.get(
            authPath(
              `vendor/permissions?vendorCode=${encodeURIComponent(vendorCode)}`
            ),
            { headers: authHeaders() }
          )
        : null;

    const requests = vendorPermissionsRequest
      ? [definitionsRequest, vendorPermissionsRequest]
      : [definitionsRequest];

    Promise.all(requests)
      .then(([definitionsRes, vendorPermissionsRes]) => {
        if (cancelled) return;

        const definitions = normalizeApiDefinitions(definitionsRes.data);
        if (!definitions.length) {
          setPermissionsError("No permissions available.");
          setPermissionsLoading(false);
          return;
        }

        setApiDefinitions(definitions);

        let assignedPermissionCodes = [];
        if (vendorPermissionsRes) {
          const vendorPermissionRows = enrichVendorPermissionRows(
            normalizeVendorPermissions(vendorPermissionsRes.data),
            definitions
          );
          assignedPermissionCodes = vendorPermissionRows.map(
            (item) => item.permissionCode
          );
          setVendorExistingPermissions(vendorPermissionRows);
        } else {
          assignedPermissionCodes = normalizePermissionList(
            permissionsVendor.permissions
          );
        }

        if (needsVendorPermissions) {
          setSelectedPermissionCodes(assignedPermissionCodes);
        } else {
          setSelectedPermissionCodes([]);
        }
        setPermissionsLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setPermissionsError(loadErrorMessage(err, "Failed to load permissions."));
        setPermissionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    showPermissionsModal,
    permissionsVendor,
    permissionsModalIsUpdate,
    permissionsModalIsRotate,
    permissionsVendorCode,
  ]);

  const dispatch = useDispatch();
  const userDetails = useSelector(
    (state) => state.authentication && state.authentication.user
  );
  const authEnvType = useSelector(
    (state) => state.authentication && state.authentication.envType
  );
  const isDevEnv = authEnvType === "DEV" || window.envType === "DEV";

  const refresh = useCallback(() => {
    dispatch({ type: "LOADER_TRUE" });
    axios
      .get(authPath("supportUsers"), { headers: authHeaders() })
      .then((res) => {
        dispatch({ type: "LOADER_FALSE" });
        if (res && res.data) {
          setTableData(res.data.body);
          toaster("success", res.data.message);
        }
      })
      .catch((err) => {
        dispatch({ type: "LOADER_FALSE" });
        if (err && err.data) {
          toaster("error", err.data.message);
        }
      });
  }, [dispatch]);

  const getCellHeaders = () => (
    <tr>
      <th>S.No.</th>
      <th>Mobile Number</th>
      <th>Name</th>
      <th>Role</th>
      {userDetails && <th>Action</th>}
      {isDevEnv && <th>Permissions</th>}
    </tr>
  );

  const inputHandler = (e, type) => {
    let value = e.target.value;
    if (type === "mobileNumber") {
      value = value.replace(/\D/g, "").slice(0, 10);
    }
    setDetails({ ...details, [type]: value });
  };

  const addUser = (e) => {
    e.preventDefault();
    const hasRequiredFields =
      details.name &&
      details.mobileNumber &&
      (!isDevEnv ||
        !isVendorRole(details.role) ||
        trimStr(details.code));

    if (!hasRequiredFields) {
      if (
        isDevEnv &&
        isVendorRole(details.role) &&
        !trimStr(details.code)
      ) {
        toaster("error", "Vendor code is required for VENDOR role.");
      }
      return;
    }

    dispatch({ type: "LOADER_TRUE" });
    const path = `${authPath("addUser")}?role=${details.role}`;
    httpRequest(path, details)
      .then((res) => {
        dispatch({ type: "LOADER_FALSE" });
        if (res && res.data) {
          if (res.data.status === "OK" || res.data.body) {
            toaster("success", res.data.message);
            refresh();
          } else {
            toaster("error", res.data.message);
          }
        }
      })
      .catch((err) => {
        dispatch({ type: "LOADER_FALSE" });
        if (err && err.data) {
          toaster("error", err.data.message);
        }
      });
  };

  const onRowEdit = (e, rowData) => {
    setShowEditPopUp(true);
    setEditRowData(rowData);
  };

  const updateRole = () => {
    dispatch(
      submitRoleAction(editRowData.mobile, editRole, () => {
        setEditRole("");
        setEditRowData({});
        setShowEditPopUp(false);
        refresh();
      })
    );
  };

  const normalizeSearchText = (value) =>
    value == null ? "" : String(value).toLowerCase().trim();

  const userMatchesSearch = (item, query) => {
    const q = normalizeSearchText(query);
    if (!q) return true;
    return (
      normalizeSearchText(item.code).includes(q) ||
      normalizeSearchText(item.name).includes(q) ||
      normalizeSearchText(item.mobile).includes(q) ||
      normalizeSearchText(item.role).includes(q)
    );
  };

  const searchQuery = searchUserValue.trim();

  const isBlank = (value) =>
    value === null || value === undefined || String(value).trim() === "";

  const getFilteredUsers = () => {
    if (!tableData) return [];
    return tableData.filter((item) => {
      if (item.role === "AGENT") return false;
      const allFieldsEmpty =
        isBlank(item.mobile) && isBlank(item.name) && isBlank(item.role);
      if (allFieldsEmpty) return false;
      if (searchQuery) return userMatchesSearch(item, searchQuery);
      return Boolean(item.mobile);
    });
  };

  const searchItem = (event) => {
    event.preventDefault();
  };

  const clearSearch = () => {
    setSearchUserValue("");
  };

  const openGeneratePermissionsModal = (item) => {
    if (!isDevEnv) {
      toaster("error", "Vendor permissions are available in DEV environment only.");
      return;
    }
    setPermissionsVendor(item);
    setPermissionsModalIsUpdate(false);
    setPermissionsModalIsRotate(false);
    setPermissionsVendorCode("");
    setRotatedVendorKeys(null);
    setRotateAfterPermissionsLoad(false);
    setShowPermissionsModal(true);
  };

  const openUpdatePermissionsModal = (item) => {
    if (!isDevEnv) {
      toaster("error", "Vendor permissions are available in DEV environment only.");
      return;
    }
    const vendorCode = resolveVendorCode(item);
    if (!vendorCode) {
      toaster("error", "Vendor code is required to update permissions.");
      return;
    }
    setPermissionsVendor(item);
    setPermissionsModalIsUpdate(true);
    setPermissionsModalIsRotate(false);
    setPermissionsVendorCode(vendorCode);
    setRotatedVendorKeys(null);
    setRotateAfterPermissionsLoad(false);
    setShowPermissionsModal(true);
  };

  const openRotatePermissionsModal = (item) => {
    if (!isDevEnv) {
      toaster("error", "Vendor permissions are available in DEV environment only.");
      return;
    }
    const vendorCode = resolveVendorCode(item);
    if (!vendorCode) {
      toaster("error", "Vendor code is required to rotate permissions.");
      return;
    }
    setPermissionsVendor(item);
    setPermissionsModalIsUpdate(false);
    setPermissionsModalIsRotate(true);
    setPermissionsVendorCode(vendorCode);
    setRotatedVendorKeys(null);
    setRotateAfterPermissionsLoad(true);
    setShowPermissionsModal(true);
  };

  const closePermissionsModal = () => {
    setShowPermissionsModal(false);
    setPermissionsVendor(null);
    setPermissionsModalIsUpdate(false);
    setPermissionsModalIsRotate(false);
    setPermissionsVendorCode("");
    setApiDefinitions([]);
    setSelectedPermissionCodes([]);
    setVendorExistingPermissions([]);
    setPermissionsLoading(false);
    setPermissionsError("");
    setRotatedVendorKeys(null);
    setRotateAfterPermissionsLoad(false);
  };

  const applyVendorKeysFromBody = (body) => {
    const keys = pickVendorKeysFromBody(body);
    if (!keys) {
      toaster("error", "Access key and secret key were not returned.");
      return false;
    }
    setRotatedVendorKeys(keys);
    return true;
  };

  const togglePermissionCode = (code) => {
    setSelectedPermissionCodes((prev) =>
      prev.includes(code)
        ? prev.filter((item) => item !== code)
        : [...prev, code]
    );
  };
//refresh vendor permissions in modal and permission alloted to vendor
  const refreshVendorPermissionsInModal = useCallback(() => {
    // After successful PUT, refresh list + checkbox selection without closing the modal.
    if (!permissionsVendorCode || !apiDefinitions.length) {
      return Promise.resolve();
    }

    return axios
      .get(
        authPath(
          `vendor/permissions?vendorCode=${encodeURIComponent(
            permissionsVendorCode
          )}`
        ),
        { headers: authHeaders() }
      )
      .then((res) => {
        const vendorPermissionRows = enrichVendorPermissionRows(
          normalizeVendorPermissions(res.data),
          apiDefinitions
        );
        setVendorExistingPermissions(vendorPermissionRows);
        setSelectedPermissionCodes(
          vendorPermissionRows.map((item) => item.permissionCode)
        );
      });
  }, [permissionsVendorCode, apiDefinitions]);

  const handleGenerateApp = () => {
    if (!permissionsVendor) return;

    if (!isDevEnv) {
      toaster("error", "Vendor permissions are available in DEV environment only.");
      return;
    }

    if (permissionsModalIsUpdate) {
      if (!permissionsVendorCode) {
        toaster("error", "Vendor code is required to update permissions.");
        return;
      }
      dispatch(
        updateVendorPermissions(
          permissionsVendorCode,
          selectedPermissionCodes,
          () => {
            refreshVendorPermissionsInModal();
            refresh();
          }
        )
      );
      return;
    }

    dispatch(
      createApp(
        {
          vendorUser: permissionsVendor,
          permissions: selectedPermissionCodes,
        },
        (body) => {
          if (applyVendorKeysFromBody(body)) {
            refresh();
          }
        }
      )
    );
  };

  const runRotatePermission = useCallback(() => {
    if (!permissionsVendor) return;
    if (!isDevEnv) {
      toaster("error", "Vendor permissions are available in DEV environment only.");
      return;
    }
    if (!selectedPermissionCodes.length) {
      toaster("error", "Select at least one permission to rotate.");
      return;
    }
    dispatch(
      createApp(
        {
          vendorUser: permissionsVendor,
          permissions: selectedPermissionCodes,
        },
        (body) => {
          if (applyVendorKeysFromBody(body)) {
            refresh();
          }
        }
      )
    );
  }, [dispatch, isDevEnv, permissionsVendor, refresh, selectedPermissionCodes]);

  useEffect(() => {
    if (
      !rotateAfterPermissionsLoad ||
      permissionsLoading ||
      permissionsError ||
      !permissionsVendor ||
      !permissionsModalIsRotate ||
      !selectedPermissionCodes.length
    ) {
      return;
    }
    setRotateAfterPermissionsLoad(false);
    runRotatePermission();
  }, [
    rotateAfterPermissionsLoad,
    permissionsLoading,
    permissionsError,
    permissionsVendor,
    permissionsModalIsRotate,
    selectedPermissionCodes.length,
    runRotatePermission,
  ]);

  const copyVendorKeyValue = (value) => {
    if (value == null || value === "") {
      toaster("error", "Nothing to copy.");
      return;
    }
    navigator.clipboard
      .writeText(String(value))
      .then(() => toaster("success", "Copied to clipboard"))
      .catch(() => toaster("error", "Failed to copy."));
  };

  const downloadRotatedVendorKeysCsv = () => {
    if (!rotatedVendorKeys) return;
    const csv = buildVendorKeysCsv(rotatedVendorKeys);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "vendor-keys.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const renderRotatedVendorKeysPanel = () => {
    if (!rotatedVendorKeys) return null;
    const rows = [
      { key: "accessKey", value: rotatedVendorKeys.accessKey },
      { key: "secretKey", value: rotatedVendorKeys.secretKey },
    ];
    return (
      <>
        <p className="operations-page__permissions-modal-note">
          Please copy the access key and secret key for future access.
        </p>
        <div className="table-responsive operations-page__permissions-table-wrap">
          <Table
            bordered
            hover
            size="sm"
            className="operations-page__table operations-page__permissions-table"
          >
            <thead>
              <tr>
                <th>Key</th>
                <th>Value</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key}>
                  <td>{row.key}</td>
                  <td className="operations-page__permissions-key-value">
                    {row.value || "—"}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="operations-page__btn operations-page__btn--primary operations-page__btn--brand operations-page__btn--sm"
                      onClick={() => copyVendorKeyValue(row.value)}
                      disabled={!row.value}
                    >
                      Copy
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        <div className="operations-page__permissions-modal-actions">
          <button
            type="button"
            className="operations-page__btn operations-page__btn--primary operations-page__btn--brand"
            onClick={downloadRotatedVendorKeysCsv}
          >
            Download
          </button>
        </div>
      </>
    );
  };

  const deleteUser = (e, item) => {
    e.preventDefault();
    dispatch({ type: "LOADER_TRUE" });
    const path = `${authPath(`removeUser/${item.mobile}`)}?role=${item.role}`;
    httpRequestDelete(path)
      .then((res) => {
        dispatch({ type: "LOADER_FALSE" });
        if (res && res.data && res.data.body) {
          toaster("success", res.data.message);
          refresh();
        } else {
          toaster("error", res.data.message);
        }
      })
      .catch((err) => {
        dispatch({ type: "LOADER_FALSE" });
        if (err && err.data) {
          toaster("error", err.data.message);
        }
      });
  };

  const getBody = () => {
    const data = getFilteredUsers();
    const firstPageIndex = (currentPage - 1) * PAGE_SIZE;
    const pageData = data.slice(firstPageIndex, firstPageIndex + PAGE_SIZE);

    return (
      pageData &&
      pageData.map((item, index) => (
        <tr key={index}>
          <td>{firstPageIndex + index + 1}</td>
          <td>{item.mobile}</td>
          <td>{item.name}</td>
          <td>{item.role}</td>
          {userDetails && (
            <td className="operations-page__row-actions">
              {item.role !== "SUPERADMIN" && (
                <>
                  <button
                    type="button"
                    className="operations-page__icon-btn"
                    onClick={(e) => onRowEdit(e, item)}
                    aria-label="Edit user"
                  >
                    <i className="fa fa-edit" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="operations-page__icon-btn"
                    onClick={(e) => deleteUser(e, item)}
                    aria-label="Delete user"
                  >
                    <i className="fa fa-trash" aria-hidden="true" />
                  </button>
                </>
              )}
            </td>
          )}
          {isDevEnv && (
          <td className="operations-page__permissions-cell">
            {isVendorRole(item.role) ? (
              vendorHasPermissions(item.permissions) ? (
                <div className="operations-page__permissions-cell-actions">
                  <button
                    type="button"
                    className="operations-page__btn operations-page__btn--primary operations-page__btn--brand operations-page__btn--sm"
                    onClick={() => openUpdatePermissionsModal(item)}
                  >
                    Update Permission
                  </button>
                  <button
                    type="button"
                    className="operations-page__btn operations-page__btn--primary operations-page__btn--brand operations-page__btn--sm"
                    onClick={() => openRotatePermissionsModal(item)}
                  >
                    Rotate Permission
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="operations-page__btn operations-page__btn--primary operations-page__btn--brand operations-page__btn--sm"
                  onClick={() => openGeneratePermissionsModal(item)}
                >
                  Generate Permission
                </button>
              )
            ) : (
              formatPermissions(item.permissions)
            )}
          </td>
          )}
        </tr>
      ))
    );
  };

  useEffect(() => {
    refresh();
  }, [refresh]);

  const filteredUsers = getFilteredUsers();

  const renderPermissionCheckboxList = (sortSelectedFirst = false) => {
    const definitions = sortSelectedFirst
      ? [...apiDefinitions].sort((a, b) => {
          const aOn = selectedPermissionCodes.includes(a.permissionCode);
          const bOn = selectedPermissionCodes.includes(b.permissionCode);
          if (aOn === bOn) return 0;
          return aOn ? -1 : 1;
        })
      : apiDefinitions;

    return (
    <div
      className="operations-page__permissions-list"
      role="group"
      aria-label="Update permissions"
    >
      {definitions.map((definition) => (
        <label
          key={definition.id}
          className="operations-page__permission-option"
        >
          <input
            type="checkbox"
            checked={selectedPermissionCodes.includes(definition.permissionCode)}
            onChange={() => togglePermissionCode(definition.permissionCode)}
          />
          <span>{definition.permissionCode}</span>
        </label>
      ))}
    </div>
    );
  };

  const renderVendorPermissionsTable = () => {
    if (!permissionsVendorCode) {
      return (
        <p className="operations-page__permissions-assigned-empty">
          Vendor code is missing.
        </p>
      );
    }

    if (!vendorExistingPermissions.length) {
      return (
        <p className="operations-page__permissions-assigned-empty">
          No permissions assigned for vendor {permissionsVendorCode}.
        </p>
      );
    }

    return (
      <div className="table-responsive operations-page__permissions-table-wrap">
        <Table
          bordered
          hover
          size="sm"
          className="operations-page__table operations-page__permissions-table"
        >
          <thead>
            <tr>
              {/* <th>Name</th> */}
              <th>Permission code</th>
              {/* <th>Service</th> */}
            </tr>
          </thead>
          <tbody>
            {vendorExistingPermissions.map((row) => (
              <tr key={row.permissionCode}>
                {/* <td>{row.name}</td> */}
                <td>{row.permissionCode}</td>
                {/* <td>{row.service}</td> */}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  return (
    <>
      <Accordion flush className="operations-page__accordion">
        <Card className="operations-page__accordion-card">
          <Accordion.Toggle as={OperationsAccordionHeader} eventKey="0">
            User list
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="0">
            <div className="operations-page__panel--inner">
              <div className="operations-page__panel-toolbar">
                <form
                  className="operations-page__search-form"
                  onSubmit={searchItem}
                >
                  <div className="operations-page__field operations-page__field--grow">
                    <label
                      className="operations-page__label"
                      htmlFor="search-user"
                    >
                      Search user
                    </label>
                    <input
                      id="search-user"
                      onChange={(e) => setSearchUserValue(e.target.value)}
                      value={searchUserValue}
                      className="operations-page__input operations-page__input--search"
                      type="text"
                      placeholder="Search by code, name, mobile, or role"
                      name="searchUser"
                    />
                  </div>
                  <div className="operations-page__actions operations-page__actions--inline">
                    <button
                      type="submit"
                      className="operations-page__btn operations-page__btn--primary operations-page__btn--brand"
                    >
                      Search
                    </button>
                    {searchQuery && (
                      <button
                        type="button"
                        className="operations-page__btn operations-page__btn--secondary"
                        onClick={clearSearch}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </form>
                <button
                  type="button"
                  className="operations-page__btn operations-page__btn--secondary"
                  onClick={refresh}
                >
                  Refresh
                </button>
              </div>
              {searchQuery && filteredUsers.length === 0 && tableData.length > 0 && (
                <p className="operations-page__search-empty">
                  No users match your search.
                </p>
              )}
              {tableData.length > 0 && filteredUsers.length > 0 && (
                <div className="proposal-dashboard__table-section operations-page__table-section">
                  <div className="table-responsive">
                    <Table
                      responsive
                      striped
                      bordered={true}
                      hover
                      variant="light"
                      className="operations-page__table"
                      headerCentered={true}
                    >
                      <thead>{getCellHeaders()}</thead>
                      <tbody>{getBody()}</tbody>
                    </Table>
                  </div>
                  <Pagination
                    className="pagination-bar"
                    currentPage={currentPage}
                    totalCount={getFilteredUsers().length}
                    pageSize={PAGE_SIZE}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
            </div>
          </Accordion.Collapse>
        </Card>

        {
        // userDetails && 
        true &&(
          <Card className="operations-page__accordion-card">
            <Accordion.Toggle as={OperationsAccordionHeader} eventKey="1">
              Create new user
            </Accordion.Toggle>
            <Accordion.Collapse eventKey="1">
              <form className="operations-page__form operations-page__panel--inner">
                <div className="operations-page__field">
                  <label className="operations-page__label" htmlFor="user-name">
                    Name
                  </label>
                  <input
                    id="user-name"
                    onChange={(e) => inputHandler(e, "name")}
                    value={details.name}
                    className="operations-page__input"
                    type="text"
                    placeholder="Enter name"
                    name="name"
                    required
                  />
                </div>
                <div className="operations-page__field">
                  <label
                    className="operations-page__label"
                    htmlFor="user-mobile"
                  >
                    Mobile number
                  </label>
                  <input
                    id="user-mobile"
                    onChange={(e) => inputHandler(e, "mobileNumber")}
                    value={details.mobileNumber}
                    className="operations-page__input"
                    type="tel"
                    inputMode="numeric"
                    placeholder="Enter mobile number"
                    name="mobileNumber"
                    maxLength={10}
                    required
                  />
                </div>
                <div className="operations-page__field">
                  <label className="operations-page__label" htmlFor="user-role">
                    Role
                  </label>
                  <div className="custom-select-container">
                    <select
                      id="user-role"
                      className="operations-page__select"
                      value={details.role}
                      onChange={(e) => inputHandler(e, "role")}
                    >
                      <option>SUPPORT</option>
                      <option>AGENT</option>
                      <option>ADMIN</option>
                      <option>OPERATIONS</option>
                      <option>SUPERUSER</option>
                      <option>MAKER</option>
                      <option>CHECKER</option>
                      {isDevEnv && <option>VENDOR</option>}
                    </select>
                  </div>
                </div>
                {isDevEnv && (
                <div className="operations-page__field">
                  <label
                    className="operations-page__label"
                    htmlFor="user-vendor-id"
                  >
                    Vendor Code
                  </label>
                  <input
                    id="user-vendor-id"
                    onChange={(e) => inputHandler(e, "code")}
                    value={details.code}
                    className="operations-page__input"
                    type="text"
                    placeholder="Enter vendor Code"
                    name="code"
                  />
                </div>
                )}
                <div className="operations-page__actions">
                  <button
                    type="button"
                    className="operations-page__btn operations-page__btn--primary"
                    onClick={addUser}
                  >
                    Add user
                  </button>
                </div>
              </form>
            </Accordion.Collapse>
          </Card>
        )}
      </Accordion>

      {isDevEnv && showPermissionsModal && (
        <MobilePopUp
          heading={
            (rotatedVendorKeys && !permissionsModalIsUpdate) ||
            permissionsModalIsRotate
              ? "Rotate Permission"
              : "Permissions"
          }
          showOnDesktop
          onClose={closePermissionsModal}
        >
          <div className="operations-page__permissions-modal">
            {rotatedVendorKeys && !permissionsModalIsUpdate ? (
              renderRotatedVendorKeysPanel()
            ) : permissionsModalIsRotate ? (
              <>
                {permissionsLoading && (
                  <p
                    className="operations-page__permissions-modal-status operations-page__permissions-modal-status--loading"
                    role="status"
                  >
                    Loading permissions…
                  </p>
                )}
                {!permissionsLoading && permissionsError && (
                  <p
                    className="operations-page__permissions-modal-status operations-page__permissions-modal-status--error"
                    role="alert"
                  >
                    {permissionsError}
                  </p>
                )}
                {!permissionsLoading &&
                  !permissionsError &&
                  !rotatedVendorKeys && (
                    <p
                      className="operations-page__permissions-modal-status operations-page__permissions-modal-status--loading"
                      role="status"
                    >
                      Rotating permission…
                    </p>
                  )}
              </>
            ) : (
              <>
            {permissionsLoading && (
              <p
                className="operations-page__permissions-modal-status operations-page__permissions-modal-status--loading"
                role="status"
              >
                Loading permissions…
              </p>
            )}
            {!permissionsLoading && permissionsError && (
              <p
                className="operations-page__permissions-modal-status operations-page__permissions-modal-status--error"
                role="alert"
              >
                {permissionsError}
              </p>
            )}
            {!permissionsLoading && !permissionsError && apiDefinitions.length > 0 && (
              <>
                <Accordion
                  flush
                  activeKey={permissionsAccordionKey}
                  onSelect={(eventKey) => {
                    if (eventKey != null) {
                      setPermissionsAccordionKey(String(eventKey));
                    }
                  }}
                  className="operations-page__accordion operations-page__permissions-modal-accordion"
                >
                  {permissionsModalIsUpdate && (
                    <Card className="operations-page__accordion-card">
                      <Accordion.Toggle
                        as={OperationsAccordionHeader}
                        eventKey="0"
                      >
                        List of permission
                      </Accordion.Toggle>
                      <Accordion.Collapse eventKey="0">
                        <div className="operations-page__permissions-accordion-panel operations-page__permissions-accordion-panel--table">
                          {renderVendorPermissionsTable()}
                        </div>
                      </Accordion.Collapse>
                    </Card>
                  )}
                  <Card className="operations-page__accordion-card">
                    <Accordion.Toggle
                      as={OperationsAccordionHeader}
                      eventKey={permissionsModalIsUpdate ? "1" : "0"}
                    >
                      {permissionsModalIsUpdate
                        ? "Update permissions"
                        : "Permission"}
                    </Accordion.Toggle>
                    <Accordion.Collapse
                      eventKey={permissionsModalIsUpdate ? "1" : "0"}
                    >
                      <div className="operations-page__permissions-accordion-panel">
                        {renderPermissionCheckboxList(
                          permissionsModalIsUpdate
                        )}
                      </div>
                    </Accordion.Collapse>
                  </Card>
                </Accordion>
                <div className="operations-page__permissions-modal-actions">
                  {permissionsModalIsUpdate ? (
                      <button
                        type="button"
                        className="operations-page__btn operations-page__btn--primary operations-page__btn--brand"
                        onClick={handleGenerateApp}
                        disabled={selectedPermissionCodes.length === 0}
                      >
                        Update Permission
                      </button>
                  ) : (
                  <button
                    type="button"
                    className="operations-page__btn operations-page__btn--primary operations-page__btn--brand"
                    onClick={handleGenerateApp}
                    disabled={selectedPermissionCodes.length === 0}
                  >
                    Generate Permission
                  </button>
                  )}
                </div>
              </>
            )}
              </>
            )}
          </div>
        </MobilePopUp>
      )}

      {showEditPopUp && (
        <MobilePopUp
          heading={editRowData.name}
          onClose={() => {
            setEditRowData({});
            setShowEditPopUp(false);
          }}
        >
          <div className="operations-page__form operations-page__modal-form">
            <div className="operations-page__field">
              <label className="operations-page__label" htmlFor="edit-role">
                Role
              </label>
              <div className="custom-select-container">
                <select
                  id="edit-role"
                  className="operations-page__select"
                  value={editRole}
                  onChange={(e) => setEditRole(e.target.value)}
                >
                  <option>SUPPORT</option>
                  <option>ADMIN</option>
                  <option>OPERATIONS</option>
                  <option>SUPERUSER</option>
                </select>
              </div>
            </div>
            <div className="operations-page__actions">
              <button
                type="button"
                className="operations-page__btn operations-page__btn--primary"
                onClick={updateRole}
              >
                Save
              </button>
            </div>
          </div>
        </MobilePopUp>
      )}
    </>
  );
};

export default Users;
